import React from "react";
import { MetricCard, SegmentedControl } from "../../../ForBrands/ui/BrandUI";
import { StatList } from "../../../ForBrands/ui/mobile/MobileUI";
import { usePhoneTheme } from "../../../ForBrands/useIsPhone";
import { SegmentedControl as PhoneSegmented } from "../../../auth/v2/pieces";
import { fmtMoney2, remainingNote } from "../allocationModel";

const POOLS = [
  { value: "rs", label: "Revenue share" },
  { value: "nil", label: "NIL" },
];
const POOL_NAME = Object.fromEntries(POOLS.map((p) => [p.value, p.label]));

// Three across while each card keeps 260px (390px in the Figma column), then
// they wrap instead of squeezing a $10,000,000.00 figure out of its card.
const Kpi = ({ label, ...card }) => (
  <div role="group" aria-label={label} className="flex min-w-0 flex-[1_1_260px] break-words">
    <MetricCard label={label} {...card} />
  </div>
);

// Figma 03b · KPI strip, for one budget pool at a time. The pool switch only
// shows once both pools are funded. On phones the three figures are the rows
// of one summary card (2415:1555), with the pool switch as the full-width
// SegmentedControl · Mobile above it (D-cs2: not drawn in the B frames, but
// the NIL figures need a home). The figures are the allocate endpoint's
// totals (useAllocationBoard `pools`), "-" until it answers for the budget;
// Remaining turns red when the locks put the pool over budget. Once the
// pool on show has a budget every label names it ("Allocated · NIL"), so
// its figures never read as the other pool's, even when the strip moves to
// NIL by itself (NIL is the only budget).
const KpiStrip = ({ pools, pool, onPool, budgetSub }) => {
  const phone = usePhoneTheme();
  const both = pools.rs.budget > 0 && pools.nil.budget > 0;
  const active = both ? pool : pools.nil.budget > 0 && !(pools.rs.budget > 0) ? "nil" : "rs";
  const { budget, allocated, remaining, players, over } = pools[active];
  const named = (label) => (budget > 0 ? `${label} · ${POOL_NAME[active]}` : label);
  const split = allocated !== null && allocated !== undefined;
  const share = budget > 0 && split ? ` · ${Math.round((100 * allocated) / budget)}% of budget` : "";
  const allocatedSub = `${players} player${players === 1 ? "" : "s"}${share}`;
  let remainingSub = "Enter a budget, then Distribute";
  if (budget > 0) remainingSub = split ? remainingNote(remaining, over) : "";
  if (phone) {
    return (
      <div className="flex flex-col gap-3">
        {both && <PhoneSegmented label="Budget pool" value={active} onChange={onPool} options={POOLS.map((p) => [p.value, p.label])} />}
        <StatList
          role="group"
          aria-label="Budget summary"
          rows={[
            { key: "budget", label: named("Total budget"), sub: budgetSub, value: fmtMoney2(budget) },
            { key: "allocated", label: named("Allocated"), sub: allocatedSub, value: fmtMoney2(allocated) },
            { key: "remaining", label: named("Remaining"), sub: remainingSub, value: fmtMoney2(remaining), valueClass: over ? "text-bm-critical" : undefined },
          ]}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {both && (
        <div className="flex justify-end">
          <SegmentedControl value={active} onChange={onPool} options={POOLS} />
        </div>
      )}
      <div className="flex flex-wrap gap-3">
        <Kpi label={named("Total budget")} value={fmtMoney2(budget)} sub={budgetSub} />
        <Kpi label={named("Allocated")} value={fmtMoney2(allocated)} sub={allocatedSub} />
        <Kpi label={named("Remaining")} value={<span className={over ? "text-bm-critical" : undefined}>{fmtMoney2(remaining)}</span>} sub={remainingSub} />
      </div>
    </div>
  );
};

export default KpiStrip;
